import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: '論理的な提案と高品質なデザインが発注の決め手',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview01.webp',
      alt: '左から、Aitriaの今野、Corekaのプロダクトマネージャーである丹治さん、Aitriaの佐々木',
      text: '左から、Aitriaの今野、Corekaのプロダクトマネージャーである丹治さん、Aitriaの佐々木',
    },
    {
      type: 'textBox',
      texts: ['―― 本プロジェクト開始の背景を教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '丹治さん',
      texts: [
        'マクロミルは「アドホックリサーチ」で大きな売上を上げ、お客様から高い信頼を得ています。一方で、労働集約型のため利益率向上の観点で課題もありました。また、経営課題として「新しいビジネスモデルの構築」が求められており、当社が保有する130万人のパネルを活用し、気軽にデータを取得できる収益性の高い新たなビジネスモデルの構築に取り組むことにしました。',
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 本プロジェクトのパートナー企業として、Aitriaにご依頼いただいた理由を教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '丹治さん',
      texts: [
        '新たなSaaS型のビジネスモデルの構築は当社にとって大きな挑戦であり、お客様に継続的に使っていただくことが重要です。そのため、直感的で使いやすいUX/UI設計が不可欠です。そこで、UX/UIに強みを持つ外部パートナーと進めることを決め、複数企業にコンペ形式で提案を依頼しました。その中でAitriaさんは、提案が論理的かつ理論に基づいており、事例のビジュアルデザインの完成度も高かった点が印象的でした。',
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '提案の度に新たな視点を提供してくれた「ベンダーではなくパートナー」',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview02.webp',
      alt: '提案の度に新たな視点を提供してくれた「ベンダーではなくパートナー」',
    },
    {
      type: 'textBox',
      texts: ['―― 課題をどのように解決していきましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '佐々木',
      texts: [
        '課題解決のためにさまざまな手段を展開しました。',
        <>
          <p>
            まず、プロダクト内の概念整理には、概念の関係性や抽象度を考慮しながらUIを設計する「
            <span className={styles.textbold}>オブジェクト指向UI（OOUI</span>
            ）」を取り入れています。たとえば、当初「ターゲット」と呼んでいた分析画面について、「その中身は何を指すのか？」を再検討。また、マーケターの業務理解を深めるため、ユーザー情報をもとにペルソナを作成し、カスタマージャーニーマップを用いて思考プロセスを可視化しました。
          </p>
          <p>
            さらに、「クリエイティブな発想を生み出すプラットフォーム」を実現するため、論文もリサーチ。その結果、
            <span className={styles.textbold}>
              情報探索には「目的の有無」や「能動的／受動的」など異なるモードがあること
            </span>
            がわかりました。また、「<span className={styles.textbold}>ベリーピッキングモデル</span>
            」という行動モデルを参考に、フィード機能や履歴機能、関連コンテンツを提示する共起機能などを設計しました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview03.webp',
      alt: '検索と探索 探索のモード 探索のモード 探索のモード',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <p>
            Corekaのパーソナリティ設計には、ユングの理論に基づく「
            <span className={styles.textbold}>ブランドアーキタイプ</span>」を活用。12のキャラクタータイプの中から「
            <span className={styles.textbold}>ジェスター（楽しさ）</span>」「
            <span className={styles.textbold}>セージ（知的さ）</span>」「
            <span className={styles.textbold}>エクスプローラー（探検）</span>
            」の組み合わせを選定し、ムードボードを作成。比較検討を重ね、「ひらめき」や「知的なイメージ」をデザインの基盤としました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview04.webp',
      alt: '12 types of brand archetypes',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview05.webp',
      alt: '丹治さん',
    },
    {
      type: 'greyBox',
      name: '丹治さん',
      texts: [
        <>
          <p>
            感覚的になりがちなデザインプロセスを論理的に説明してくれたので、信頼して進められました。
            <span className={styles.textbold}>
              「Why（なぜそうするのか）」の論理性と「How（どのように実現するのか）」のクリエイティブのバランスが絶妙
            </span>
            で、データ重視の弊社とも相性が良かったと感じています。
          </p>
          <p>
            特に、ブランドアーキタイプの明快なプロセスのおかげで、メンバー間で共通認識を持てたことは大きな成果です。追加機能やデザイン検討だけでなく、
            <span className={styles.textbold}>事業設計にもポジティブな影響を与えました</span>
            。現在進めているCorekaの広告展開にも、このパーソナリティを活用できています。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '佐々木',
      texts: [
        <>
          <p>
            私たちは指示通りに作るのではなく、
            <span className={styles.textbold}>
              より良い形を提案し、納得感のある成果物を提供することを重視しています
            </span>
            。そのため、デザイン組織として「<span className={styles.textbold}>プロトタイピング思考</span>
            」が根付いています。これは、複数のバリエーションを試作し、それぞれのメリット・デメリットを明確に言語化しながら最適なデザインを探ります。この考えを基本にすることで、より納得度の高いデザインを生み出せると考えています。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― Aitriaの対応全体について、どのように感じられましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '丹治さん',
      texts: [
        <>
          <p>
            非常に満足度が高かったです。<span className={styles.textbold}>単なるベンダーではなく、パートナー</span>
            のように感じました。
            <span className={styles.textbold}>依頼に対して単一の提案ではなく、必ず複数の選択肢を提示</span>
            してくださり、新たな視点や気づきを得る機会が多かったです。
          </p>
          <p>
            現在も運用担当の方からさまざまなご提案をいただいており、Aitriaの文化や考え方が会社全体に根付いていることを実感しています。
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '直感的なUX/UIが高評価　今後は機能拡充が課題',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview06.webp',
      alt: '直感的なUX/UIが高評価 今後は機能拡充が課題',
    },
    {
      type: 'textBox',
      texts: ['―― プロダクトの中で、個人的に気に入っている部分を挙げるとしたらどこですか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '丹治さん',
      texts: [
        <>
          <p>
            ロゴデザインですね。Corekaのコンセプトとデザインの楽しさが融合し、非常に納得感のある仕上がりになりました。制作プロセス自体も楽しかったです。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview07.webp',
      alt: 'Coreka Logo Story',
    },
    {
      type: 'greyBox',
      name: '今野',
      texts: [
        <>
          <p>
            ロゴ制作は、ブランドアーキタイプの「
            <span className={styles.textbold}>ジェスター×セージ×エクスプローラー</span>
            」の人格をもとに進めました。ジェスターの「楽しさ」とセージの「知的さ」を表現しながら、Corekaが支援する「発想」から「ひらめき」というキーワードを導き出しました。そこから
            <span className={styles.textbold}>四角い星のモチーフ</span>が生まれ、
            <span className={styles.textbold}>弓矢のようなモチーフを組み合わせる</span>
            という発想が展開されました。背景には
            <span className={styles.textbold}>神話のエピソードも組み込まれています</span>。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― ユーザーの皆様からの反応やフィードバックがあれば教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '丹治さん',
      texts: [
        <>
          <p>
            <span className={styles.textbold}>UX/UIに対する不満を指摘されたことはなく、非常に高評価</span>
            です。特に、動作のスムーズさと「マニュアルなしでも直感的に使える」点が好評です。「画面を見れば使い方が分かる」と言われることが多く、当初掲げていた「SaaSとして直感的に使える」「つまずきをなくす」という目標がしっかり実現できたと感じています。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview08.webp',
      alt: '満足度分析 ブランドマップ インデックスツリー分析 時系列分析（購入金額） ブランドランキング',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/coreka/interview09.webp',
      alt: 'Coreka ホーム画面',
    },
    {
      type: 'textBox',
      texts: ['―― 最後に、今後の目標やマイルストーンについてお聞かせください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '丹治さん',
      texts: [
        <>
          <p>
            まだ道半ばで、ようやく6合目に差し掛かった感覚です。基本の構造は整いましたが、ヒアリングを重ねる中で足りない機能も明確になってきました。
            <span className={styles.textbold}>今後は世界観を維持しながらの機能の拡充が課題</span>
            です。引き続きAitriaさんと連携しながら、より完成度の高いプロダクトへと進化させていきたいと考えています。
          </p>
        </>,
      ],
    },
    {
      type: 'imageLink',
      src: '/images/img/cases/coreka/banner.webp',
      srcsp: '/images/img/cases/coreka/banner.webp',
      alt: 'Coreka ホーム画面',
      link: 'https://www.macromill.com/service/platform/coreka/',
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
